// Typed wrapper for the /v1/merchants/:id/beneficiaries endpoint.
//
// BFF rule (CLAUDE.md rule 1): all calls go through apiClient — the single
// axios instance that injects Authorization: Bearer <token> (in-memory) and
// X-Merchant-ID (localStorage-mirrored). Never construct a new axios instance.
import { apiClient } from "./client";
import type { Payout } from "./payouts";

// ---------------------------------------------------------------------------
// Response interfaces
// ---------------------------------------------------------------------------

/**
 * RawBeneficiary — the JSON shape as returned by the backend.
 * The backend beneficiaryItem DTO suppresses provider_beneid and surfaces
 * only is_registered (T-06-01 information disclosure mitigate, Plan 06-01).
 * This type is only used at the deserialization boundary inside listBeneficiaries.
 *
 * CR-01 fix: the backend serialises the display name as `name` (json:"name" tag
 * on beneficiaryItem). Using `label` here caused every Beneficiary.label to be
 * undefined at runtime. Field renamed to `name` to match the wire format.
 */
interface RawBeneficiary {
  id: number;
  name: string;
  /**
   * Raw bank account number as registered with the provider. Surfaced so the
   * Merchant Detail beneficiaries list can disambiguate two beneficiaries
   * with the same name and so operators can verify a row before referencing
   * its id in a CSV mass-payout. The UI masks all but the last four digits
   * (utils/format.ts → maskAccount) so the full PAN never lands in a
   * screenshot or screen-share.
   */
  account_no: string;
  ifsc: string;
  /** True when the beneficiary has a registered provider_beneid on the backend. */
  is_registered: boolean;
  /** Contact email collected at AddBeneficiary; nullable pre-migration-022. */
  email: string | null;
  /** Contact phone collected at AddBeneficiary; nullable pre-migration-022. */
  phone: string | null;
  /**
   * Timestamp of the last successful IDFC penny-drop / AccountVerification.
   * NULL until the operator runs Verify from the BeneficiaryDetail page.
   * A bene with provider_beneid set but verified_at NULL is the typical
   * trigger for an upstream code 900 (IDFC's side never confirmed the
   * account is live).
   */
  verified_at: string | null;
}

/**
 * Beneficiary — the typed beneficiary record exposed to UI consumers.
 *
 * is_registered signals whether the beneficiary has been registered with the
 * payment provider. validateRows.ts (Plan 06-03) requires a registration signal
 * to flag unregistered beneficiary rows as invalid before submission.
 *
 * Note: the backend returns is_registered (not provider_beneid) per T-06-01.
 * The UploadBatchModal (Plan 06-05) maps this to the validateRows Beneficiary
 * interface by adapting is_registered to provider_beneid (null when false).
 */
export interface Beneficiary {
  id: number;
  /** Display name for the beneficiary shown in validation preview tables. */
  label: string;
  /**
   * Raw account number from the backend. Display sites MUST run it through
   * utils/format.ts → maskAccount() before rendering — the unmasked value is
   * kept on the interface so support flows (e.g. "the row with account ending
   * 7890") can match against the last-four without re-fetching.
   */
  account_no: string;
  /** IFSC code for the beneficiary's bank account. */
  ifsc: string;
  /** True when the beneficiary is registered with the provider (T-06-14). */
  is_registered: boolean;
  /** Contact email captured at registration; null when never provided. */
  email: string | null;
  /** Contact phone captured at registration; null when never provided. */
  phone: string | null;
  /**
   * ISO timestamp of the last successful penny-drop verification. null
   * means "never verified" — the panel renders a Verify CTA in that case.
   */
  verified_at: string | null;
}

// ---------------------------------------------------------------------------
// API function
// ---------------------------------------------------------------------------

/**
 * listBeneficiaries — GET /v1/merchants/:merchantId/beneficiaries
 *
 * Returns the merchant's beneficiaries for client-side validation pre-load
 * (RESEARCH Pattern 7). The UploadBatchModal calls this on open so that
 * validateRows can resolve beneficiary IDs without making per-row API calls.
 *
 * Requires viewer+ membership (D-03 RBAC — backend enforces RequireMerchantScope
 * + RequireMembershipRole(viewer), wired under authed group, JWT-only).
 *
 * No bigint conversion needed — beneficiary amounts are not present in this
 * response (CLAUDE.md rule 6 applies to money fields only).
 */
export async function listBeneficiaries(
  merchantId: number,
): Promise<Beneficiary[]> {
  const resp = await apiClient.get<{ beneficiaries: RawBeneficiary[] }>(
    `/v1/merchants/${merchantId}/beneficiaries`,
  );
  // CR-01 fix: map the backend `name` field to the SPA's `label` field so that
  // adaptBeneficiaries (UploadBatchModal) and validateRows always receive a
  // populated label instead of undefined.
  return resp.data.beneficiaries.map((b) => ({
    id: b.id,
    label: b.name,
    account_no: b.account_no,
    ifsc: b.ifsc,
    is_registered: b.is_registered,
    email: b.email ?? null,
    phone: b.phone ?? null,
    verified_at: b.verified_at,
  }));
}

/**
 * CreateBeneficiaryPayload — POST /v1/merchants/:id/beneficiaries body.
 * bank_account_id selects the rail whose provider credentials register the
 * beneficiary with the bank rail. All fields are required by the backend.
 */
export interface CreateBeneficiaryPayload {
  bank_account_id: number;
  name: string;
  account_no: string;
  ifsc: string;
  email: string;
  phone: string;
}

/**
 * createBeneficiary — POST /v1/merchants/:merchantId/beneficiaries
 *
 * Registers a new beneficiary with the payment provider. Returns the newly-
 * created row so callers (notably the CSV-v2 auto-register flow) can resolve
 * the new id without a follow-up GET round-trip. The backend's handler
 * already returns `toBeneficiaryItem(*bene)` on 201 — this client just
 * stopped throwing it away.
 */
export async function createBeneficiary(
  merchantId: number,
  payload: CreateBeneficiaryPayload,
): Promise<Beneficiary> {
  const resp = await apiClient.post<RawBeneficiary>(
    `/v1/merchants/${merchantId}/beneficiaries`,
    payload,
  );
  return {
    id: resp.data.id,
    label: resp.data.name,
    account_no: resp.data.account_no,
    ifsc: resp.data.ifsc,
    is_registered: resp.data.is_registered,
    email: resp.data.email ?? null,
    phone: resp.data.phone ?? null,
    verified_at: resp.data.verified_at,
  };
}

/**
 * BeneficiaryDetail — bundled response from
 * GET /v1/merchants/:id/beneficiaries/:bid.
 *
 * Single round-trip: the beneficiary row + every payout that was
 * dispatched to it (newest first). Backs the BeneficiaryDetail page.
 *
 * Payout list reuses the same Payout type as the main /payouts page,
 * so the renderer can be the same (status pill, amount, txn id, etc.)
 * with no DTO drift across surfaces.
 */
export interface BeneficiaryDetail {
  beneficiary: Beneficiary;
  payouts: Payout[];
}

/**
 * RawPayoutInBeneficiary — wire shape for a payout row inside the
 * detail response. Same shape as RawPayout in payouts.ts; duplicated
 * here so the module stays self-contained at the deserialization
 * boundary, matching the pattern already established in batches.ts.
 */
interface RawPayoutInBeneficiary {
  id: number;
  merchant_id: number;
  batch_id: number | null;
  request_id: string;
  status: "needs_approval" | "pending" | "success" | "failure";
  amount_minor: number | bigint;
  currency: string;
  txn_mode: number;
  provider_txn_id: string | null;
  utr_no: string | null;
  error_code: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * getBeneficiary — GET /v1/merchants/:merchantId/beneficiaries/:beneficiaryId
 *
 * Normalises every amount_minor at the deserialization boundary so the
 * caller always receives bigint (CLAUDE.md rule 6 — never parseFloat).
 *
 * Server errors:
 *   400 beneficiary_id_invalid   — malformed :bid path segment
 *   404 beneficiary_not_found    — bad bene id OR cross-tenant probe
 *   500 beneficiary_detail       — unexpected backend error
 */
export async function getBeneficiary(
  merchantId: number,
  beneficiaryId: number,
): Promise<BeneficiaryDetail> {
  const resp = await apiClient.get<{
    beneficiary: RawBeneficiary;
    payouts: RawPayoutInBeneficiary[];
  }>(`/v1/merchants/${merchantId}/beneficiaries/${beneficiaryId}`);
  return {
    beneficiary: {
      id: resp.data.beneficiary.id,
      label: resp.data.beneficiary.name,
      account_no: resp.data.beneficiary.account_no,
      ifsc: resp.data.beneficiary.ifsc,
      is_registered: resp.data.beneficiary.is_registered,
      email: resp.data.beneficiary.email ?? null,
      phone: resp.data.beneficiary.phone ?? null,
      verified_at: resp.data.beneficiary.verified_at,
    },
    payouts: resp.data.payouts.map((p) => ({
      ...p,
      amount_minor: BigInt(p.amount_minor),
    })),
  };
}

/**
 * VerifyBeneficiaryResult — response of penny-drop AccountVerification.
 *
 * account_name is what the destination bank returned for the account.
 * Operators compare it against beneficiary.label to catch typos before
 * sending real money. verified_at is the timestamp the backend wrote
 * to beneficiaries.verified_at — surfaced so the panel can render the
 * "verified" badge inline without a follow-up GET.
 */
export interface VerifyBeneficiaryResult {
  beneficiary: Beneficiary;
  account_name: string;
  verified_at: string;
}

/**
 * verifyBeneficiary — POST /v1/merchants/:merchantId/beneficiaries/:bid/verify
 *
 * Runs a penny-drop on the beneficiary via the supplied bank rail's
 * IDFC credentials. operator+ required at the membership layer
 * (penny-drop is a billable upstream op).
 *
 * Server errors of interest:
 *   404 beneficiary_not_found     — bad bene id OR cross-tenant probe
 *   422 invalid_bank_account      — bank_account_id is not this merchant's
 *   422 verify_failed             — destination bank does not see the
 *                                   account as live; the bene is NOT
 *                                   safe to send money to
 *   502 verify_provider_error     — transport-level provider failure;
 *                                   retry, then escalate
 */
export async function verifyBeneficiary(
  merchantId: number,
  beneficiaryId: number,
  bankAccountId: number,
): Promise<VerifyBeneficiaryResult> {
  const resp = await apiClient.post<{
    beneficiary: RawBeneficiary;
    account_name: string;
    verified_at: string;
  }>(
    `/v1/merchants/${merchantId}/beneficiaries/${beneficiaryId}/verify`,
    { bank_account_id: bankAccountId },
  );
  return {
    beneficiary: {
      id: resp.data.beneficiary.id,
      label: resp.data.beneficiary.name,
      account_no: resp.data.beneficiary.account_no,
      ifsc: resp.data.beneficiary.ifsc,
      is_registered: resp.data.beneficiary.is_registered,
      email: resp.data.beneficiary.email ?? null,
      phone: resp.data.beneficiary.phone ?? null,
      verified_at: resp.data.beneficiary.verified_at,
    },
    account_name: resp.data.account_name,
    verified_at: resp.data.verified_at,
  };
}
